import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import StarRating from "../../components/feedback/StarRating";
import {
  PentesterFeedbackItem,
  ProgramFeedbackSubmission,
  useGetFeedbackStatusQuery,
  useSubmitFeedbackMutation
} from "../../utils/api/endpoints/program-feedback/programFeedbackApi";
import { FiAlertCircle, FiCheckCircle, FiThumbsUp, FiMessageSquare, FiArrowLeft } from "react-icons/fi";
import useProgram from "../../utils/hooks/programs/useProgram";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { useStoreSelector } from "../../utils/hooks/hooks";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

/**
 * Program Feedback Page - Multi-step feedback flow for programs
 */
const ProgramFeedbackPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const runIdParam = queryParams.get("runId") || queryParams.get("run_id");
  const runId = runIdParam ? parseInt(runIdParam, 10) : undefined;
  const programId = id ? parseInt(id, 10) : 0;

  const userRole = useStoreSelector((state) => state.user.credentials.userRole);
  const isFeedbackAllowed =
    userRole === UserRole.BUSINESS || userRole === UserRole.BUSINESS_ADMINISTRATOR;

  const { program, isLoading: programLoading } = useProgram(programId);
  const { data: feedbackStatus, isLoading: statusLoading } = useGetFeedbackStatusQuery(
    { programId, runId }, 
    { skip: !programId || !isFeedbackAllowed }
  );
  usePageTitle(`Feedback - ${program?.title || "Program"}${runIdParam ? ` - Run #${runIdParam}` : ""}`);

  const isRunFeedback = Boolean(feedbackStatus?.run?.run_title || runIdParam);
  const targetLabel = isRunFeedback ? "Run" : "Program";
  const targetLabelLower = isRunFeedback ? "run" : "program";

  // State management
  const [currentPage, setCurrentPage] = useState<
    "selection" | "satisfied-rating" | "feedback-rating" | "feedback-researchers" | "improve" | "thank-you"
  >("selection");

  const [overallRating, setOverallRating] = useState(0);
  const [overallComments, setOverallComments] = useState("");
  const [pentesterRatings, setPentesterRatings] = useState<Record<number, PentesterFeedbackItem>>({});
  const [improvementReasons, setImprovementReasons] = useState<string[]>([]);
  const [improvementDetails, setImprovementDetails] = useState("");
  const [isSkippedCycle, setIsSkippedCycle] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<"satisfied" | "detailed" | "skipped" | null>(null);

  const [submitFeedbackMutation, { isLoading: isSubmittingFeedback }] = useSubmitFeedbackMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper functions
  const updatePentesterRating = (
    pentesterId: number,
    pentesterUsername: string,
    field: keyof PentesterFeedbackItem,
    value: number | string
  ) => {
    setPentesterRatings((prev) => ({
      ...prev,
      [pentesterId]: {
        ...prev[pentesterId],
        pentester_user_id: pentesterId,
        pentester_username: pentesterUsername,
        [field]: value
      }
    }));
  };

  const toggleImprovementReason = (reason: string) => {
    setImprovementReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  // Submit handlers
  const handleSatisfiedSubmit = async () => {
    if (overallRating === 0) {
      setError(`Please provide an overall ${targetLabelLower} rating.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setIsSkippedCycle(false);

    const feedbackData: ProgramFeedbackSubmission = {
      program_id: programId,
      run_id: runId,
      overall_program_rating: overallRating,
      overall_program_comments: overallComments
    };

    try {
      await submitFeedbackMutation(feedbackData).unwrap();
      setCurrentPage("thank-you");
    } catch (err: any) {
      setError(err?.data?.message || err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDetailedSubmit = async () => {
    if (overallRating === 0) {
      setError(`Please provide an overall ${targetLabelLower} rating.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setIsSkippedCycle(false);

    const pentesterFeedbacksArray = Object.values(pentesterRatings).filter(
      (pf) =>
        pf.technical_depth_rating > 0 ||
        pf.accuracy_rating > 0 ||
        pf.communication_rating > 0 ||
        pf.business_context_rating > 0
    );

    const feedbackData: ProgramFeedbackSubmission = {
      program_id: programId,
      run_id: runId,
      overall_program_rating: overallRating,
      overall_program_comments: overallComments,
      pentester_feedbacks: pentesterFeedbacksArray,
      improvement_reasons: improvementReasons,
      improvement_details: improvementDetails,
    };

    try {
      await submitFeedbackMutation(feedbackData).unwrap();
      setCurrentPage("thank-you");
    } catch (err: any) {
      setError(err?.data?.message || err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setIsSkippedCycle(true);

    const feedbackData: ProgramFeedbackSubmission = {
      program_id: programId,
      run_id: runId,
      overall_program_rating: 0,
      is_skipped: true
    };

    try {
      await submitFeedbackMutation(feedbackData).unwrap();
      setCurrentPage("thank-you");
    } catch (err: any) {
      setError(err?.data?.message || err.message || "Failed to skip feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (programLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0F1E]">
        <div className="text-white">Loading program...</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0F1E]">
        <div className="text-white">Program not found</div>
      </div>
    );
  }

  // Helper Components
  const SimpleProgressBar = ({ activeStep, steps = ["Start", "Rating", "Complete"] }: { activeStep: number, steps?: string[] }) => {
    return (
      <div className="flex items-center justify-center gap-4 bg-[#0A1628] px-8 py-4 rounded-t-xl">
        {steps.map((label, index) => {
          const stepNum = index + 1;
          const isActive = activeStep >= stepNum;
          return (
            <div key={label} className={`flex flex-col items-center w-32`}>
              <span className={`${isActive ? 'text-white' : 'text-gray-500'} text-xs mb-2`}>{label}</span>
              <div className={`h-1 w-full rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-700'}`}></div>
            </div>
          );
        })}
      </div>
    );
  };

  const PageHeader = ({ title, subtitle }: { title: string, subtitle?: string }) => (
    <div className="bg-[#0A1628] px-8 py-10 text-center rounded-b-xl">
      <div className="inline-block px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-medium mb-4">
        {program?.title} {feedbackStatus?.run?.run_title ? `| ${feedbackStatus.run.run_title}` : (runIdParam ? `| Run #${runIdParam}` : "")}
      </div>
      <h3 className="text-2xl font-semibold text-white mb-2">{title}</h3>
      {subtitle && <p className="text-gray-400 text-sm">{subtitle}</p>}
    </div>
  );

  // Loading state
  if (programLoading || statusLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Block access for non-authorized roles
  if (!isFeedbackAllowed) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiAlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              Only Business Owners and Organization Admins can submit feedback.
            </p>
            <button
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to {targetLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Block access if feedback is not requested and not submitted
  if (!statusLoading && !feedbackStatus?.is_requested && !feedbackStatus?.has_submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiAlertCircle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Feedback Not Available</h2>
            <p className="text-gray-600 mb-6">
              Feedback can only be submitted when requested by an admin.
            </p>
            <button
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to {targetLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Block access if feedback already submitted
  if (feedbackStatus?.has_submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiCheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Feedback Already Submitted</h2>
            <p className="text-gray-600 mb-6">
              You have already submitted feedback for this {targetLabelLower} on{" "}
              {feedbackStatus.feedback?.createdAt && new Date(feedbackStatus.feedback.createdAt).toLocaleDateString()}.
            </p>
            <button
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to {targetLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/dashboard/programs/${programId}`)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FiArrowLeft className="w-5 h-5" />
          <span>Back to {targetLabel}</span>
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 m-6">
              <div className="flex items-center gap-3">
                <FiAlertCircle className="text-red-500 w-5 h-5 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Selection Page */}
          {currentPage === "selection" && (
            <div>
              <PageHeader
                title="How was your experience?"
                subtitle="Your feedback helps us improve our security testing services"
              />
              <div className="p-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Satisfied Card */}
                  <button
                    onClick={() => {
                      setSelectedFlow("satisfied");
                      setCurrentPage("satisfied-rating");
                    }}
                    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 p-8 text-left transition-all hover:shadow-xl hover:scale-105 border-2 border-transparent hover:border-green-200"
                  >
                    <div className="relative z-10">
                      <div className="mb-4 inline-flex rounded-full bg-green-100 p-4">
                        <FiThumbsUp className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-gray-900">I'm Satisfied</h3>
                      <p className="text-sm text-gray-600">
                        Everything went well. I'd like to share my positive experience.
                      </p>
                    </div>
                    <div className="absolute bottom-0 right-0 h-32 w-32 translate-x-8 translate-y-8 rounded-full bg-green-200 opacity-20 transition-transform group-hover:scale-150"></div>
                  </button>

                  {/* Feedback Card */}
                  <button
                    onClick={() => {
                      setSelectedFlow("detailed");
                      setCurrentPage("feedback-rating");
                    }}
                    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 text-left transition-all hover:shadow-xl hover:scale-105 border-2 border-transparent hover:border-blue-200"
                  >
                    <div className="relative z-10">
                      <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                        <FiMessageSquare className="h-8 w-8 text-blue-600" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-gray-900">I Have Feedback</h3>
                      <p className="text-sm text-gray-600">
                        I have suggestions or concerns I'd like to share for improvement.
                      </p>
                    </div>
                    <div className="absolute bottom-0 right-0 h-32 w-32 translate-x-8 translate-y-8 rounded-full bg-blue-200 opacity-20 transition-transform group-hover:scale-150"></div>
                  </button>

                  {/* Skip Card */}
                  <button
                    onClick={() => {
                      setSelectedFlow("skipped");
                      handleSkipSubmit();
                    }}
                    disabled={isSubmitting || isSubmittingFeedback}
                    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-50 to-slate-100 p-8 text-left transition-all hover:shadow-xl hover:scale-105 border-2 border-transparent hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="relative z-10">
                      <div className="mb-4 inline-flex rounded-full bg-gray-200 p-4">
                        <FiAlertCircle className="h-8 w-8 text-gray-600" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-gray-900">Skip Feedback</h3>
                      <p className="text-sm text-gray-600">
                        I'd like to skip providing feedback for this cycle.
                      </p>
                    </div>
                    <div className="absolute bottom-0 right-0 h-32 w-32 translate-x-8 translate-y-8 rounded-full bg-gray-300 opacity-20 transition-transform group-hover:scale-150"></div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Satisfied Rating Page */}
          {currentPage === "satisfied-rating" && (
            <div>
              <SimpleProgressBar activeStep={2} steps={["Start", `${targetLabel} Rating`, "Complete"]} />
              <PageHeader
                title="Rate Your Experience"
                subtitle={`We're glad you're satisfied! Please share your overall ${targetLabelLower} rating.`}
              />
              <div className="p-12">
                <div className="max-w-2xl mx-auto space-y-8">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Overall {targetLabel} Rating
                    </label>
                    <StarRating
                      rating={overallRating}
                      onRatingChange={setOverallRating}
                      label=""
                      size="lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Comments (Optional)
                    </label>
                    <textarea
                      value={overallComments}
                      onChange={(e) => setOverallComments(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 p-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-gray-50"
                      rows={4}
                      placeholder={`Share what went well with this ${targetLabelLower}...`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-none p-8 bg-white border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => {
                    setSelectedFlow(null);
                    setCurrentPage("selection");
                  }}
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSatisfiedSubmit}
                  disabled={isSubmitting || isSubmittingFeedback || overallRating === 0}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting || isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
                  {!(isSubmitting || isSubmittingFeedback) && <FiCheckCircle className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {/* Feedback Rating Page (Detailed Flow - Step 1) */}
          {currentPage === "feedback-rating" && (
            <div>
              <SimpleProgressBar
                activeStep={1}
                steps={
                  overallRating >= 3
                    ? ["Start", `${targetLabel} Rating`, "Researcher Rating", "Complete"]
                    : ["Start", `${targetLabel} Rating`, "Researcher Rating", "Improvement", "Complete"]
                }
              />
              <PageHeader
                title={`Rate the ${targetLabel}`}
                subtitle={`Help us understand your overall experience with this security assessment ${targetLabelLower}`}
              />
              <div className="p-12">
                <div className="max-w-2xl mx-auto space-y-8">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Overall {targetLabel} Rating <span className="text-red-500">*</span>
                    </label>
                    <StarRating
                      rating={overallRating}
                      onRatingChange={setOverallRating}
                      label=""
                      size="lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Comments {overallRating > 0 && overallRating < 3 ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(Optional)</span>}
                    </label>
                    <textarea
                      value={overallComments}
                      onChange={(e) => setOverallComments(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 p-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-gray-50"
                      rows={4}
                      placeholder={`Share your thoughts about this ${targetLabelLower}...`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-none p-8 bg-white border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => {
                    setSelectedFlow(null);
                    setCurrentPage("selection");
                  }}
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (overallRating === 0) {
                      setError(`Please provide an overall ${targetLabelLower} rating.`);
                      return;
                    }
                    if (overallRating < 3 && !overallComments.trim()) {
                      setError("Please provide comments explaining your rating.");
                      return;
                    }
                    setError(null);
                    setCurrentPage("feedback-researchers");
                  }}
                  disabled={overallRating === 0}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Rate Researchers
                </button>
              </div>
            </div>
          )}

          {/* Feedback Researchers Page (Detailed Flow - Step 2) */}
          {currentPage === "feedback-researchers" && (
            <div>
              <SimpleProgressBar
                activeStep={2}
                steps={
                  overallRating >= 3
                    ? ["Start", `${targetLabel} Rating`, "Researcher Rating", "Complete"]
                    : ["Start", `${targetLabel} Rating`, "Researcher Rating", "Improvement", "Complete"]
                }
              />
              <PageHeader
                title="Rate the Researchers"
                subtitle={`Help us understand how each researcher performed during this ${targetLabelLower}`}
              />
              <div className="p-12">
                <div className="max-w-3xl mx-auto space-y-6">
                  {feedbackStatus?.eligible_pentesters && feedbackStatus.eligible_pentesters.length > 0 ? (
                    feedbackStatus.eligible_pentesters.map((pentester: any) => (
                      <div key={pentester.user_id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                          Researcher: {pentester.display_name || pentester.username}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-2">Technical Depth <span className="text-red-500">*</span></label>
                            <StarRating
                              rating={pentesterRatings[pentester.user_id]?.technical_depth_rating || 0}
                              onRatingChange={(v) => updatePentesterRating(pentester.user_id, pentester.username, 'technical_depth_rating', v)}
                              label=""
                              size="sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-2">Accuracy <span className="text-red-500">*</span></label>
                            <StarRating
                              rating={pentesterRatings[pentester.user_id]?.accuracy_rating || 0}
                              onRatingChange={(v) => updatePentesterRating(pentester.user_id, pentester.username, 'accuracy_rating', v)}
                              label=""
                              size="sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-2">Communication <span className="text-red-500">*</span></label>
                            <StarRating
                              rating={pentesterRatings[pentester.user_id]?.communication_rating || 0}
                              onRatingChange={(v) => updatePentesterRating(pentester.user_id, pentester.username, 'communication_rating', v)}
                              label=""
                              size="sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-2">Business Context <span className="text-red-500">*</span></label>
                            <StarRating
                              rating={pentesterRatings[pentester.user_id]?.business_context_rating || 0}
                              onRatingChange={(v) => updatePentesterRating(pentester.user_id, pentester.username, 'business_context_rating', v)}
                              label=""
                              size="sm"
                            />
                          </div>
                        </div>

                        <div className="mt-6">
                          <label className="block text-xs font-semibold text-gray-600 mb-2">Comments (Optional)</label>
                          <textarea
                            value={pentesterRatings[pentester.user_id]?.comments || ""}
                            onChange={(e) => updatePentesterRating(pentester.user_id, pentester.username, 'comments', e.target.value)}
                            className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            rows={2}
                            placeholder="Specific feedback for this researcher..."
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No researchers assigned to this {targetLabelLower}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-none p-8 bg-white border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage("feedback-rating")}
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (feedbackStatus?.eligible_pentesters && feedbackStatus.eligible_pentesters.length > 0) {
                      for (const pentester of feedbackStatus.eligible_pentesters) {
                        const pr = pentesterRatings[pentester.user_id];
                        if (!pr || !pr.technical_depth_rating || !pr.accuracy_rating || !pr.communication_rating || !pr.business_context_rating) {
                          setError(`Please complete all rating categories (Technical Depth, Accuracy, Communication, Business Context) for researcher ${pentester.display_name || pentester.username}.`);
                          return;
                        }
                      }
                    }
                    setError(null);
                    if (overallRating < 3) {
                      setCurrentPage("improve");
                    } else {
                      handleDetailedSubmit();
                    }
                  }}
                  disabled={isSubmitting || isSubmittingFeedback}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting || isSubmittingFeedback ? "Submitting..." : (overallRating < 3 ? "Next: Improvement Areas" : "Submit Feedback")}
                  {overallRating >= 3 && !(isSubmitting || isSubmittingFeedback) && <FiCheckCircle className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {/* Improve Page (Detailed Flow - Step 3) */}
          {currentPage === "improve" && (
            <div>
              <SimpleProgressBar activeStep={3} steps={["Start", `${targetLabel} Rating`, "Researcher Rating", "Improvement", "Complete"]} />
              <PageHeader
                title="Areas for Improvement"
                subtitle="Help us understand what could be better"
              />
              <div className="p-12">
                <div className="max-w-2xl mx-auto space-y-8">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      What areas need improvement? (Select all that apply) <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-3">
                      {[
                        "Communication & Responsiveness",
                        "Technical Depth & Expertise",
                        "Report Quality & Clarity",
                        "Timeline & Deadlines",
                        "Scope Coverage",
                        "Business Context Understanding"
                      ].map((reason) => (
                        <label
                          key={reason}
                          className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={improvementReasons.includes(reason)}
                            onChange={() => toggleImprovementReason(reason)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{reason}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Details (Optional)
                    </label>
                    <textarea
                      value={improvementDetails}
                      onChange={(e) => setImprovementDetails(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 p-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-gray-50"
                      rows={4}
                      placeholder="Please provide specific details about what could be improved..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex-none p-8 bg-white border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage("feedback-researchers")}
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (improvementReasons.length === 0) {
                      setError("Please select at least one area needing improvement.");
                      return;
                    }
                    handleDetailedSubmit();
                  }}
                  disabled={isSubmitting || isSubmittingFeedback}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting || isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
                  {!(isSubmitting || isSubmittingFeedback) && <FiCheckCircle className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}



          {/* Thank You Page */}
          {currentPage === "thank-you" && (
            <div>
              <PageHeader
                title={isSkippedCycle ? "Feedback Skipped" : "Thank You!"}
                subtitle={isSkippedCycle ? "You have successfully skipped providing feedback for this cycle" : "Your feedback has been submitted successfully"}
              />
              <div className="p-12">
                <div className="max-w-2xl mx-auto text-center space-y-6">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${isSkippedCycle ? 'bg-gray-100' : 'bg-green-100'}`}>
                    <FiCheckCircle className={`w-10 h-10 ${isSkippedCycle ? 'text-gray-600' : 'text-green-600'}`} />
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xl font-semibold text-gray-900">
                      {isSkippedCycle ? "Cycle Skipped" : "Feedback Received"}
                    </h4>
                    <p className="text-gray-600">
                      {isSkippedCycle 
                        ? "Your preference to skip feedback for this cycle has been recorded. You can continue managing your program." 
                        : "We appreciate you taking the time to share your experience. Your feedback helps us continuously improve our security testing services."}
                    </p>
                  </div>

                  <div className="pt-6 flex gap-4 justify-center">
                    <button
                      onClick={() => navigate("/dashboard/programs")}
                      className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      View All Programs
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/programs/${programId}`)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Back to {targetLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramFeedbackPage;